<template>
  <div id="whatsapp-verification-dialog" class="professional-dialog">
    <!-- Header -->
    <div class="dialog-header">
      <div class="header-content">
        <div class="header-icon">
          <v-icon color="white" size="24">mdi-whatsapp</v-icon>
        </div>
        <div class="header-info">
          <h3 class="header-title">{{ pageTitle }}</h3>
          <p class="header-subtitle">Secure your account with WhatsApp verification</p>
        </div>
      </div>
      <v-btn
        icon
        color="white"
        class="close-btn"
        @click="closePrompt"
      >
        <v-icon size="20">mdi-close</v-icon>
      </v-btn>
    </div>

    <!-- Content -->
    <div class="dialog-content">
      <!-- Loading State -->
      <div v-if="SearchFlag" class="loading-container">
        <v-progress-circular
          indeterminate
          size="60"
          width="4"
          color="primary"
        ></v-progress-circular>
        <p class="loading-text">Processing your request...</p>
      </div>

      <!-- Step 1: Enter WhatsApp Number -->
      <div v-else-if="!OtpSendFlag" class="verification-step">
        <div class="step-header">
          <div class="step-indicator">
            <div class="step-number active">1</div>
            <div class="step-line"></div>
            <div class="step-number">2</div>
          </div>
          <div class="step-info">
            <h4 class="step-title">Enter WhatsApp Number</h4>
            <p class="step-description">We'll send a verification code to your WhatsApp</p>
          </div>
        </div>

        <v-form
          ref="form1"
          v-model="valid1"
          lazy-validation
          @submit.prevent="sendOtp"
          class="verification-form"
        >
          <div class="input-section">
            <div class="input-group">
              <label class="input-label">
                <v-icon small color="success" class="mr-2">mdi-whatsapp</v-icon>
                WhatsApp Number
                <span class="required-asterisk">*</span>
              </label>
              <v-text-field
                v-model="WhatsappNo"
                :rules="WhatsappNoRules"
                outlined
                dense
                v-mask="'##########'"
                placeholder="Enter your 10-digit number"
                prepend-inner-icon="mdi-phone"
                class="professional-input"
              >
                <template v-slot:append>
                  <v-icon color="success">mdi-whatsapp</v-icon>
                </template>
              </v-text-field>
            </div>
          </div>

          <div class="action-section">
            <v-btn
              type="submit"
              :disabled="!valid1 || SendOtpLoadingFlag"
              :loading="SendOtpLoadingFlag"
              color="success"
              large
              depressed
              class="action-btn"
              @click.prevent="sendOtp"
            >
              <v-icon left>mdi-send</v-icon>
              Send Verification Code
            </v-btn>
          </div>
        </v-form>
      </div>

      <!-- Step 2: Verify OTP -->
      <div v-else-if="OtpSendFlag && !VerifyOtpFlag" class="verification-step">
        <div class="step-header">
          <div class="step-indicator">
            <div class="step-number completed">
              <v-icon small color="white">mdi-check</v-icon>
            </div>
            <div class="step-line completed"></div>
            <div class="step-number active">2</div>
          </div>
          <div class="step-info">
            <h4 class="step-title">Verify Code</h4>
            <p class="step-description">
              Enter the 6-digit code sent to
              <strong>+91 {{ WhatsappNo }}</strong>
            </p>
          </div>
        </div>

        <v-form
          ref="form2"
          v-model="valid2"
          lazy-validation
          @submit.prevent="verifyOtp"
          class="verification-form"
        >
          <div class="input-section">
            <div class="input-group">
              <label class="input-label">
                <v-icon small color="primary" class="mr-2">mdi-shield-key</v-icon>
                Verification Code
                <span class="required-asterisk">*</span>
              </label>
              <v-text-field
                v-model="VerificationCode"
                :rules="VerificationCodeRules"
                :counter="6"
                outlined
                dense
                placeholder="Enter 6-digit code"
                prepend-inner-icon="mdi-key"
                class="professional-input otp-input"
                maxlength="6"
              ></v-text-field>

              <!-- Resend Option -->
              <div class="resend-section">
                <p class="resend-text">
                  Didn't receive the code?
                  <v-btn
                    text
                    small
                    color="primary"
                    class="resend-btn"
                    @click="sendOtp"
                    :disabled="SendOtpLoadingFlag"
                  >
                    Resend Code
                  </v-btn>
                </p>
              </div>
            </div>
          </div>

          <div class="action-section">
            <v-btn
              text
              large
              class="secondary-btn"
              @click="OtpSendFlag = false"
            >
              <v-icon left>mdi-arrow-left</v-icon>
              Back
            </v-btn>

            <v-btn
              type="submit"
              :disabled="!valid2 || VerifyOtpLoadingFlag"
              :loading="VerifyOtpLoadingFlag"
              color="primary"
              large
              depressed
              class="action-btn"
              @click.prevent="verifyOtp"
            >
              <v-icon left>mdi-check-circle</v-icon>
              Verify & Complete
            </v-btn>
          </div>
        </v-form>
      </div>

      <!-- Success State -->
      <div v-else-if="VerifyOtpFlag" class="success-state">
        <div class="success-icon">
          <v-icon size="80" color="success">mdi-check-circle</v-icon>
        </div>
        <h3 class="success-title">Verification Successful!</h3>
        <p class="success-message">
          Your WhatsApp number has been verified and updated successfully.
        </p>
      </div>
    </div>

    <!-- Footer -->
    <div class="dialog-footer">
      <div class="security-info">
        <v-icon small color="grey">mdi-shield-check</v-icon>
        <span class="security-text">Your information is secure and encrypted</span>
      </div>
    </div>
  </div>
</template>


<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {},
  props: {
    addRecordPrompt: {
      type: Boolean,
      required: true,
    },
    pageId: {
      required: false,
    },
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      valid3: true,

      LoadingFlag: false,
      SearchFlag: false,
      ResultFlag: false,

      WhatsappNo: "",
      WhatsappNoRules: [(v) => !!v || "Whatsapp number is required"],
      Otp: "",

      VerificationCode: "",
      VerificationCodeRules: [(v) => !!v || "OTP is required"],

      OtpSendFlag: false,
      VerifyOtpFlag: false,
      SubmitFlag: false,

      SendOtpButtonFlag: false,

      SendOtpLoadingFlag: false,
      VerifyOtpLoadingFlag: false,
      SubmitLoadingFlag: false,

      pageTitle: "Verify WhatsApp number",
      pageDescription: "",

      pageContent: "",

      rows: [],

      MembershipId: "",
      MembershipIdRules: [(v) => !!v || "Membership Id is required"],

      MobileNo: "",
      MobileNoRules: [(v) => !!v || "Mobile number is required"],

      NewPassword: "",
      NewPasswordRules: [(v) => !!v || "New password is required"],

      ConfirmPassword: "",
      ConfirmPasswordRules: [(v) => !!v || "Confirm password is required"],

      RefId: "",
    };
  },
  computed: {},
  watch: {},
  created() {
    // this.initialize()
  },
  methods: {
    validateForm() {
      console.log("validateForm called");
      this.$refs.form1.validate();
    },
    resetForm() {
      console.log("resetForm called");
      this.$refs.form1.reset();
      //   this.$refs.ProfilePicture.reset();
    },
    resetValidation() {
      console.log("resetValidation called");
      this.$refs.form1.resetValidation();
    },
    closePrompt() {
      console.log("closePrompt called");
      this.$emit("hideAddRecordPrompt", this.ResultFlag);
    },
    refreshPageData() {
      console.log("refreshPageData called");
      var pageId = this.pageId;
      console.log("pageId=" + pageId);
      //   this.getMemberDetails();
    },
    sendOtp() {
      console.log("sendOtp is called");

      var validate = this.$refs.form1.validate();
      console.log("validate=" + validate);
      /*
      validate = false;
      this.SendOtpLoadingFlag = true;
      setTimeout(() => {
        this.SendOtpLoadingFlag = false;
        this.OtpSendFlag = true;
      }, 2000);
      */

      if (validate == true) {
        this.SubmitFlag = true;
        this.SendOtpLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");

        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/send/whatsapp-otp";
        console.log("add_url=" + add_url);

        var upload = {
          UserInterface: 1,
          WhatsappNumber: this.WhatsappNo,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.SendOtpLoadingFlag = false;
            thisIns.SubmitFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.OtpSendFlag = true;
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SendOtpLoadingFlag = false;
            thisIns.SubmitFlag = false;
          });
      } else {
        var message = "";
        if (validate == false) {
          message += "Kindly fill the required fields. ";
        }
        this.sweetAlert("error", message, false);
      }
    },

    verifyOtp() {
      console.log("verifyOtp is called");

      var validate = this.$refs.form2.validate();
      console.log("validate=" + validate);

      if (validate == true) {
        this.VerifyOtpLoadingFlag = true;
        this.ResultFlag = false;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/verify/whatsapp-otp";
        var upload = {
          UserInterface: 1,
          MobileNumber: this.WhatsappNo,
          OtpNumber: this.VerificationCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.VerifyOtpLoadingFlag = false;
            // thisIns.ShowFormFlag = true;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.ResultFlag = true;
              thisIns.VerifyOtpLoadingFlag = false;
              thisIns.closePrompt();
            } else {
              thisIns.sweetAlert("error", output, false);
              // thisIns.MemberTypeId = 23;
              // thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.VerifyOtpLoadingFlag = false;
          });
      } else {
        var message = "";
        if (validate == false) {
          message += "Kindly fill the required fields. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style scoped>
.professional-dialog {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f8f9fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Header Styles */
.dialog-header {
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  position: relative;
  overflow: hidden;
}

.dialog-header::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 100px;
  height: 100px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  transform: translate(30%, -30%);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-info {
  color: white;
}

.header-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.2;
}

.header-subtitle {
  font-size: 0.9rem;
  margin: 4px 0 0;
  opacity: 0.9;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

/* Content Styles */
.dialog-content {
  flex: 1;
  padding: 32px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* Loading Container */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 48px 0;
}

.loading-text {
  font-size: 1.1rem;
  color: #666;
  margin: 0;
}

/* Verification Step */
.verification-step {
  max-width: 520px;
  margin: 0 auto;
  width: 100%;
}

/* Step Header */
.step-header {
  text-align: center;
  margin-bottom: 48px;
}

.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  gap: 8px;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
  border: 2px solid #e0e0e0;
  color: #999;
  background: white;
  transition: all 0.3s ease;
}

.step-number.active {
  background: #25D366;
  border-color: #25D366;
  color: white;
  box-shadow: 0 0 0 4px rgba(37, 211, 102, 0.2);
}

.step-number.completed {
  background: #4CAF50;
  border-color: #4CAF50;
  color: white;
}

.step-line {
  width: 60px;
  height: 2px;
  background: #e0e0e0;
  transition: all 0.3s ease;
}

.step-line.completed {
  background: #4CAF50;
}

.step-info {
  text-align: center;
}

.step-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 8px;
}

.step-description {
  color: #666;
  font-size: 1rem;
  margin: 0;
  line-height: 1.5;
}

/* Form Styles */
.verification-form {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e8eaed;
}

.input-section {
  margin-bottom: 32px;
}

.input-group {
  margin-bottom: 24px;
}

.input-label {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 12px;
  font-size: 0.95rem;
}

.required-asterisk {
  color: #e74c3c;
  margin-left: 4px;
}

.professional-input {
  margin-top: 8px;
}

.professional-input >>> .v-input__control .v-input__slot {
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
}

.professional-input >>> .v-text-field--outlined fieldset {
  border-color: #e0e0e0 !important;
  transition: border-color 0.3s ease;
}

.professional-input >>> .v-input--is-focused .v-text-field--outlined fieldset {
  border-color: #25D366 !important;
  border-width: 2px !important;
}

.otp-input >>> input {
  text-align: center;
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 2px;
}

/* Resend Section */
.resend-section {
  margin-top: 16px;
  text-align: center;
}

.resend-text {
  color: #666;
  font-size: 0.9rem;
  margin: 0;
}

.resend-btn {
  text-transform: none !important;
  font-weight: 600;
  padding: 0 8px !important;
  min-width: auto !important;
}

/* Action Section */
.action-section {
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  align-items: center;
}

.action-btn {
  min-width: 180px !important;
  height: 48px !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  transition: all 0.3s ease !important;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2) !important;
}

.secondary-btn {
  border-radius: 12px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  color: #666 !important;
}

/* Success State */
.success-state {
  text-align: center;
  padding: 48px 32px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  max-width: 400px;
  margin: 0 auto;
}

.success-icon {
  margin-bottom: 24px;
}

.success-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 16px;
}

.success-message {
  color: #666;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
}

/* Footer */
.dialog-footer {
  padding: 20px 32px;
  background: white;
  border-top: 1px solid #e8eaed;
  display: flex;
  align-items: center;
  justify-content: center;
}

.security-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 0.85rem;
}

.security-text {
  opacity: 0.8;
}

/* Responsive Design */
@media (max-width: 768px) {
  .dialog-content {
    padding: 24px 16px;
  }

  .verification-form {
    padding: 24px 16px;
  }

  .header-title {
    font-size: 1.25rem;
  }

  .header-subtitle {
    font-size: 0.85rem;
  }

  .action-section {
    flex-direction: column;
  }

  .action-btn {
    width: 100% !important;
  }

  .step-indicator {
    gap: 4px;
  }

  .step-line {
    width: 40px;
  }
}

@media (max-width: 480px) {
  .dialog-header {
    padding: 16px 20px;
  }

  .header-content {
    gap: 12px;
  }

  .header-icon {
    width: 40px;
    height: 40px;
  }

  .success-state {
    padding: 32px 16px;
  }

  .step-number {
    width: 32px;
    height: 32px;
    font-size: 0.9rem;
  }
}

/* Animation Classes */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.slide-enter-active, .slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter, .slide-leave-to {
  transform: translateX(30px);
}

/* Focus States */
.professional-input >>> .v-input--is-focused .v-label {
  color: #25D366 !important;
}

.professional-input >>> .v-input--is-focused .v-input__prepend-inner .v-icon {
  color: #25D366 !important;
}

/* Loading Button States */
.action-btn >>> .v-btn__loading {
  color: rgba(255, 255, 255, 0.8) !important;
}

/* Custom Scrollbar */
.dialog-content::-webkit-scrollbar {
  width: 6px;
}

.dialog-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.dialog-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.dialog-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
